import { useEffect, useState } from 'react';
import { pollworkerSurveyJsTheme } from 'shared/src/components/pollworkerSurveyJsTheme';
import { Alert, AlertDescription } from 'shared/src/components/ui/new';
import { Model, type SurveyModel, settings } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'shared/src/components/surveyJsCustomConfiguration';
import 'survey-core/defaultV2.min.css';
import 'shared/src/PollworkerSettings.css';

type FormViewerProps = {
  surveyFormSchema: SurveyModel;
  readOnly?: boolean;
  allowSubmit?: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: the data is dynamica based on the form schema, so we can't type it
  onValidate: (formDataJson: Record<string, any>, valid: (isValid: boolean) => void) => void;
  // biome-ignore lint/suspicious/noExplicitAny: the data is dynamica based on the form schema, so we can't type it
  data?: any;
  onComplete: (sender: SurveyModel) => Promise<boolean>;
  customerId?: string;
  agreementText?: string;
};

let canComplete = true;

export default function PollworkerApplicationForm({
  data,
  onValidate,
  onComplete,
  customerId,
  agreementText,
  readOnly = false,
  allowSubmit = true,
  surveyFormSchema: compiledForm,
}: FormViewerProps) {
  const [model, setModel] = useState<SurveyModel>();
  const [globalValidationError, setGlobalValidationError] = useState<string>();

  useEffect(() => {
    if (!agreementText || !customerId) return;

    settings.web.encodeUrlParams = false;

    const model = new Model(compiledForm);

    if (readOnly) {
      for (const q of model.getAllQuestions()) {
        q.readOnly = true;
      }
    }

    model.setVariable('iAgreeCopy', agreementText);
    model.setVariable('pollworkerWebsiteApiHostname', import.meta.env.VITE_POLLWORKER_DOMAIN);
    model.setVariable('customerId', customerId);

    if (data) model.data = data;

    model.showCompletedPage = true;
    model.showQuestionNumbers = false;
    model.questionsOnPageMode = 'singlePage';
    model.focusFirstQuestionAutomatic = false;
    model.showCompleteButton = !readOnly;

    model.onServerValidateQuestions.add((survey, { data, complete }) => {
      model.validate();

      onValidate(data, (isValid) => {
        canComplete = isValid;

        if (!isValid) {
          window.scroll(0, 0);
          setGlobalValidationError('We could not process your application. Please try again.');
        }

        complete();
      });
    });

    model.onCompleting.add((sender, options) => {
      options.allow = canComplete;

      if (canComplete && allowSubmit) {
        onComplete(sender).then((success) => {
          if (success) return;

          model.clear(false); // Reinit the form but keep the user's data
        });
      }
    });

    model.css = pollworkerSurveyJsTheme;

    setModel(model);
  }, [readOnly, onValidate, compiledForm, onComplete, allowSubmit, data, agreementText, customerId]);

  if (!model) return null;

  return (
    <>
      {globalValidationError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{globalValidationError}</AlertDescription>
        </Alert>
      ) : null}
      <Survey model={model} />
    </>
  );
}
